import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { INDIAN_STATES, deriveFromGstin, validateGstin } from '../utils/gst';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';

const INDUSTRIES = [
  'Advertising & Marketing','Architecture & Design','Automotive','Banking & Finance',
  'Consulting','E-Commerce','Education','Entertainment & Media','Fashion & Apparel',
  'Food & Beverage','Healthcare','Hospitality & Travel','IT & Software','Legal',
  'Logistics & Supply Chain','Manufacturing','NGO / Non-Profit','Real Estate',
  'Retail','Telecommunications','Other',
];

const EMPTY_FORM = {
  name:'', type:'simple', state:'Maharashtra', gstin:'', pan:'',
  industry:'', website:'', linkedin:'', report_template:'full',
  address_line1:'', address_line2:'', city:'', pincode:'', country:'India',
  contact_person:'', phone:'', email:'',
  secondary_contact_name:'', secondary_contact_phone:'', secondary_contact_email:'',
  accounts_contact_name:'', accounts_contact_phone:'', accounts_contact_email:'',
  marketing_contact_name:'', marketing_contact_phone:'', marketing_contact_email:'',
  decision_maker_name:'', decision_maker_phone:'', decision_maker_email:'', decision_maker_birthday:'',
  notes:'',
};

function clientToForm(c) {
  return {
    name: c.name||'', type: c.type||'simple', state: c.state||'Maharashtra',
    gstin: c.gstin||'', pan: c.pan||'', industry: c.industry||'',
    website: c.website||'', linkedin: c.linkedin||'', report_template: c.report_template||'full',
    address_line1: c.address_line1||'', address_line2: c.address_line2||'',
    city: c.city||'', pincode: c.pincode||'', country: c.country||'India',
    contact_person: c.contact_person||'', phone: c.phone||'', email: c.email||'',
    secondary_contact_name: c.secondary_contact_name||'',
    secondary_contact_phone: c.secondary_contact_phone||'',
    secondary_contact_email: c.secondary_contact_email||'',
    accounts_contact_name: c.accounts_contact_name||'',
    accounts_contact_phone: c.accounts_contact_phone||'',
    accounts_contact_email: c.accounts_contact_email||'',
    marketing_contact_name: c.marketing_contact_name||'',
    marketing_contact_phone: c.marketing_contact_phone||'',
    marketing_contact_email: c.marketing_contact_email||'',
    decision_maker_name: c.decision_maker_name||'',
    decision_maker_phone: c.decision_maker_phone||'',
    decision_maker_email: c.decision_maker_email||'',
    decision_maker_birthday: c.decision_maker_birthday ? c.decision_maker_birthday.slice(0,10) : '',
    notes: c.notes||'',
  };
}

function ContactBlock({ label, name, phone, email, birthday }) {
  if (!name && !phone && !email) return null;
  return (
    <div style={{ background:'var(--surface2)', borderRadius:'var(--radius-sm)', padding:'10px 12px', minWidth:0 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>{label}</div>
      {name && <div style={{ fontWeight:600, fontSize:13, color:'var(--text1)', marginBottom:3 }}>{name}</div>}
      {phone && <div style={{ fontSize:12, color:'var(--text2)' }}>📞 {phone}</div>}
      {email && <div style={{ fontSize:12, color:'var(--text2)', wordBreak:'break-all' }}>✉ {email}</div>}
      {birthday && <div style={{ fontSize:11, color:'var(--accent)', marginTop:4 }}>🎂 {new Date(birthday+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>}
    </div>
  );
}

function ClientForm({ form, setForm, tab, setTab }) {
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = (k, extra={}) => <input value={form[k]} onChange={e=>f(k,e.target.value)} {...extra} />;

  const gstinResult = validateGstin(form.gstin);
  const gstinDerived = deriveFromGstin(form.gstin);
  const gstinLocked = !!gstinDerived; // state is auto-filled when GSTIN resolves

  function handleGstinChange(val) {
    const derived = deriveFromGstin(val);
    setForm(p => ({
      ...p,
      gstin: val,
      ...(derived ? { state: derived.state } : {}),
    }));
  }

  return (
    <>
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {['Company','Address','Contacts','Notes'].map(t => (
          <button key={t} className={tab===t?'btn-primary btn-sm':'btn btn-sm'} onClick={()=>setTab(t)}>{t}</button>
        ))}
      </div>

      {tab==='Company' && <>
        <div className="form-grid">
          <div className="field"><label>Company name *</label>{inp('name',{placeholder:'e.g. Acme Corp'})}</div>
          <div className="field"><label>Industry</label>
            <select value={form.industry} onChange={e=>f('industry',e.target.value)}>
              <option value="">— Select industry —</option>
              {INDUSTRIES.map(i=><option key={i}>{i}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="field"><label>Website</label>{inp('website',{placeholder:'https://company.com', type:'url'})}</div>
          <div className="field"><label>LinkedIn</label>{inp('linkedin',{placeholder:'https://linkedin.com/company/...'})}</div>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>GSTIN</label>
            <input
              value={form.gstin}
              onChange={e=>handleGstinChange(e.target.value.toUpperCase())}
              placeholder="27AAAAA0000A1Z5"
              maxLength={15}
              style={form.gstin && !gstinResult.valid ? {borderColor:'var(--red)'} : {}}
            />
            {form.gstin && !gstinResult.valid && (
              <div style={{fontSize:11,color:'var(--red)',marginTop:3}}>{gstinResult.error}</div>
            )}
            {gstinDerived && (
              <div style={{fontSize:11,color:'var(--green)',marginTop:3}}>
                ✓ State code <strong>{gstinDerived.code}</strong> — {gstinDerived.state}
              </div>
            )}
            {!form.gstin && (
              <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>Leave blank for unregistered clients</div>
            )}
          </div>
          <div className="field"><label>PAN</label>{inp('pan',{placeholder:'AABCU9603R'})}</div>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>State {gstinLocked && <span style={{fontSize:10,color:'var(--accent)',fontWeight:600}}> (auto from GSTIN)</span>}</label>
            <select
              value={form.state}
              onChange={e=>f('state',e.target.value)}
              disabled={gstinLocked}
              style={gstinLocked ? {opacity:0.7,cursor:'not-allowed'} : {}}
            >
              {INDIAN_STATES.map(s=><option key={s}>{s}</option>)}
            </select>
            {gstinLocked && (
              <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>Clear GSTIN to edit state manually</div>
            )}
          </div>
          <div className="field"><label>Type</label>
            <select value={form.type} onChange={e=>f('type',e.target.value)}>
              <option value="simple">Standard</option>
              <option value="hierarchical">Hierarchical (persons + stakeholders)</option>
            </select>
          </div>
        </div>
        <div className="field"><label>Report template</label>
          <select value={form.report_template} onChange={e=>f('report_template',e.target.value)}>
            <option value="summary">Summary only</option>
            <option value="summary_person">Summary + Person wise</option>
            <option value="full">Full (Summary + Person + Stakeholder)</option>
          </select>
        </div>
      </>}

      {tab==='Address' && <>
        <div className="field"><label>Street / Building</label>{inp('address_line1',{placeholder:'123, MG Road, Andheri West'})}</div>
        <div className="field"><label>Area / Locality</label>{inp('address_line2',{placeholder:'Andheri West'})}</div>
        <div className="form-grid">
          <div className="field"><label>City</label>{inp('city',{placeholder:'Mumbai'})}</div>
          <div className="field"><label>Pincode</label>{inp('pincode',{placeholder:'400053',maxLength:6})}</div>
        </div>
        <div className="field"><label>Country</label>{inp('country')}</div>
      </>}

      {tab==='Contacts' && <>
        <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, marginBottom:8 }}>PRIMARY CONTACT</div>
        <div className="form-grid">
          <div className="field"><label>Name</label>{inp('contact_person',{placeholder:'Rahul Sharma'})}</div>
          <div className="field"><label>Phone</label>{inp('phone',{placeholder:'+91 98765 43210'})}</div>
        </div>
        <div className="field"><label>Email</label>{inp('email',{type:'email',placeholder:'billing@client.com'})}</div>

        <hr className="divider" style={{margin:'12px 0'}}/>
        <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, marginBottom:8 }}>SECONDARY CONTACT</div>
        <div className="form-grid">
          <div className="field"><label>Name</label>{inp('secondary_contact_name',{placeholder:'Priya Mehta'})}</div>
          <div className="field"><label>Phone</label>{inp('secondary_contact_phone',{placeholder:'+91 ...'})}</div>
        </div>
        <div className="field"><label>Email</label>{inp('secondary_contact_email',{type:'email'})}</div>

        <hr className="divider" style={{margin:'12px 0'}}/>
        <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, marginBottom:8 }}>ACCOUNTS CONTACT</div>
        <div className="form-grid">
          <div className="field"><label>Name</label>{inp('accounts_contact_name',{placeholder:'Accounts Manager'})}</div>
          <div className="field"><label>Phone</label>{inp('accounts_contact_phone',{placeholder:'+91 ...'})}</div>
        </div>
        <div className="field"><label>Email</label>{inp('accounts_contact_email',{type:'email',placeholder:'accounts@client.com'})}</div>

        <hr className="divider" style={{margin:'12px 0'}}/>
        <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, marginBottom:8 }}>MARKETING CONTACT</div>
        <div className="form-grid">
          <div className="field"><label>Name</label>{inp('marketing_contact_name',{placeholder:'Marketing Head'})}</div>
          <div className="field"><label>Phone</label>{inp('marketing_contact_phone',{placeholder:'+91 ...'})}</div>
        </div>
        <div className="field"><label>Email</label>{inp('marketing_contact_email',{type:'email',placeholder:'marketing@client.com'})}</div>

        <hr className="divider" style={{margin:'12px 0'}}/>
        <div style={{ fontSize:11, color:'var(--text3)', fontWeight:700, marginBottom:8 }}>DECISION MAKER</div>
        <div className="form-grid">
          <div className="field"><label>Name</label>{inp('decision_maker_name',{placeholder:'CEO / MD'})}</div>
          <div className="field"><label>Phone</label>{inp('decision_maker_phone',{placeholder:'+91 ...'})}</div>
        </div>
        <div className="form-grid">
          <div className="field"><label>Email</label>{inp('decision_maker_email',{type:'email'})}</div>
          <div className="field"><label>Birthday</label>{inp('decision_maker_birthday',{type:'date'})}</div>
        </div>
      </>}

      {tab==='Notes' && <>
        <div className="field">
          <label>Notes</label>
          <textarea value={form.notes} onChange={e=>f('notes',e.target.value)} rows={8} placeholder="Payment terms, preferences, important context about this client…" style={{resize:'vertical'}}/>
        </div>
      </>}
    </>
  );
}

function renderNodes(nodes, parentId=null, clientId, depth=0, handlers) {
  const { setNodeModal, setNodeForm, setDeleteNode } = handlers;
  return nodes.filter(n=>n.parent_id===parentId).map(n=>{
    const isPerson = depth === 0;
    const children = nodes.filter(c=>c.parent_id===n.id);
    return (
      <div key={n.id} style={isPerson ? {marginBottom:6} : {}}>
        <div style={{
          display:'flex',alignItems:'center',gap:8,
          padding: isPerson ? '8px 10px' : '5px 10px 5px 28px',
          background: isPerson ? 'var(--accent-light)' : 'transparent',
          borderRadius: isPerson && !children.length ? 'var(--radius-sm)' : isPerson ? 'var(--radius-sm) var(--radius-sm) 0 0' : 0,
          border: isPerson ? '1px solid rgba(45,107,228,0.18)' : 'none',
          borderBottom: !isPerson ? '1px solid var(--border)' : undefined,
        }}>
          {!isPerson && <span style={{color:'var(--text3)',fontSize:11,marginRight:2}}>└</span>}
          {isPerson
            ? <span style={{fontSize:10,fontWeight:700,color:'var(--accent)',background:'rgba(45,107,228,0.12)',padding:'1px 7px',borderRadius:10,flexShrink:0}}>Person</span>
            : <span style={{fontSize:10,color:'var(--text3)',width:64,flexShrink:0}}>Stakeholder</span>
          }
          <span style={{fontWeight: isPerson ? 700 : 500, fontSize: isPerson ? 13 : 12, flex:1, color: isPerson ? 'var(--text1)' : 'var(--text2)'}}>{n.name}</span>
          <button className="btn-sm" style={{fontSize:10}} onClick={()=>{ setNodeModal({type:'add',clientId,parentId:n.id}); setNodeForm({name:'',label:'Stakeholder'}); }}>+ child</button>
          <button className="btn-sm" style={{fontSize:10}} onClick={()=>{ setNodeModal({type:'edit',clientId,node:n}); setNodeForm({name:n.name,label:n.label}); }}>✎</button>
          <button className="btn-danger btn-sm" style={{fontSize:10}} onClick={()=>setDeleteNode({clientId,nodeId:n.id,name:n.name})}>✕</button>
        </div>
        {children.length>0 && (
          <div style={isPerson ? {border:'1px solid rgba(45,107,228,0.18)',borderTop:'none',borderRadius:'0 0 var(--radius-sm) var(--radius-sm)'} : {}}>
            {renderNodes(nodes, n.id, clientId, depth+1, handlers)}
          </div>
        )}
      </div>
    );
  });
}

export default function ClientsPage() {
  const qc = useQueryClient();
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addTab, setAddTab] = useState('Company');
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editTab, setEditTab] = useState('Company');
  const [rateModal, setRateModal] = useState(null);
  const [rateForm, setRateForm] = useState({ category_id:'', unit_price:'' });
  const [bulkRows, setBulkRows] = useState([{ name:'', default_price:'', gst_percent:'18' }]);
  const [rateTab, setRateTab] = useState('categories');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatForm, setEditCatForm] = useState({ name:'', default_price:'', gst_percent:'' });
  const [nodeModal, setNodeModal] = useState(null);
  const [nodeForm, setNodeForm] = useState({ name:'', label:'Person' });
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleteNode, setDeleteNode] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedCards, setExpandedCards] = useState({});

  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });
  const { data: rateCategories=[] } = useQuery({
    queryKey: ['categories', rateModal?.id],
    queryFn: ()=>api.get(`/categories?clientId=${rateModal.id}`).then(r=>r.data),
    enabled: !!rateModal,
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter(c => !q || c.name.toLowerCase().includes(q) || (c.industry||'').toLowerCase().includes(q));
  }, [clients, search]);

  const addClient = useMutation({
    mutationFn: d=>api.post('/clients',d),
    onSuccess: ()=>{ toast.success('Client added'); qc.invalidateQueries(['clients']); setAddModal(false); setAddForm(EMPTY_FORM); setAddTab('Company'); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to add client'),
  });
  const updateClient = useMutation({
    mutationFn: ({id,...d})=>api.patch(`/clients/${id}`,d),
    onSuccess: ()=>{ toast.success('Client updated'); qc.invalidateQueries(['clients']); setEditClient(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to update client'),
  });
  const removeClient = useMutation({
    mutationFn: id=>api.delete(`/clients/${id}`),
    onSuccess: ()=>{ toast.success('Client removed'); qc.invalidateQueries(['clients']); setDeleteClient(null); },
    onError: ()=>toast.error('Failed to remove client'),
  });
  const addNode = useMutation({
    mutationFn: ({clientId,data})=>api.post(`/clients/${clientId}/nodes`,data),
    onSuccess: ()=>{ toast.success('Added'); qc.invalidateQueries(['clients']); setNodeModal(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to add'),
  });
  const editNode = useMutation({
    mutationFn: ({clientId,nodeId,data})=>api.patch(`/clients/${clientId}/nodes/${nodeId}`,data),
    onSuccess: ()=>{ toast.success('Updated'); qc.invalidateQueries(['clients']); setNodeModal(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to update'),
  });
  const removeNode = useMutation({
    mutationFn: ({clientId,nodeId})=>api.delete(`/clients/${clientId}/nodes/${nodeId}`),
    onSuccess: ()=>{ qc.invalidateQueries(['clients']); setDeleteNode(null); },
    onError: ()=>toast.error('Failed to remove'),
  });
  const upsertRate = useMutation({
    mutationFn: d=>api.put('/categories/client-rates',d),
    onSuccess: ()=>{ toast.success('Rate saved'); qc.invalidateQueries(['categories']); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to save rate'),
  });
  const addClientCat = useMutation({
    mutationFn: d=>api.post('/categories',d),
    onSuccess: ()=>{ qc.invalidateQueries(['categories']); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to add category'),
  });
  const removeClientCat = useMutation({
    mutationFn: id=>api.delete(`/categories/${id}`),
    onSuccess: ()=>{ toast.success('Category removed'); qc.invalidateQueries(['categories']); },
    onError: ()=>toast.error('Failed to remove category'),
  });
  const editClientCat = useMutation({
    mutationFn: ({id,...d})=>api.patch(`/categories/${id}`,d),
    onSuccess: ()=>{ toast.success('Rate updated'); qc.invalidateQueries(['categories']); setEditingCatId(null); },
    onError: ()=>toast.error('Failed to update rate'),
  });

  async function saveBulkRows() {
    const valid = bulkRows.filter(r=>r.name.trim()&&r.default_price);
    if (!valid.length) { toast.error('Add at least one item with name and price'); return; }
    let added = 0;
    for (const row of valid) {
      try { await api.post('/categories', { name:row.name.trim(), default_price:row.default_price, gst_percent:row.gst_percent||'18', client_id:rateModal.id }); added++; }
      catch { toast.error(`Failed to add: ${row.name}`); }
    }
    if (added) { toast.success(`${added} categor${added===1?'y':'ies'} added`); qc.invalidateQueries(['categories']); setBulkRows([{name:'',default_price:'',gst_percent:'18'}]); }
  }

  function submitNode() {
    if (!nodeForm.name.trim()) { toast.error('Enter a name'); return; }
    const clientId = nodeModal.clientId;
    if (nodeModal.type === 'add') addNode.mutate({ clientId, data: { name: nodeForm.name, label: nodeForm.label, parent_id: nodeModal.parentId||null } });
    else editNode.mutate({ clientId, nodeId: nodeModal.node.id, data: { name: nodeForm.name, label: nodeForm.label } });
  }

  const nodeHandlers = { setNodeModal, setNodeForm, setDeleteNode };

  return (
    <div>
      <div className="section-head">
        <div>
          <h2>CRM — Clients</h2>
          <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>{clients.length} client{clients.length!==1?'s':''}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients…" style={{fontSize:12,padding:'6px 10px',width:180}}/>
          <button className="btn-primary" onClick={()=>{ setAddModal(true); setAddTab('Company'); }}>+ Add client</button>
        </div>
      </div>

      {filtered.map(c => {
        const expanded = expandedCards[c.id];
        const hasContacts = c.contact_person||c.secondary_contact_name||c.accounts_contact_name||c.marketing_contact_name||c.decision_maker_name;
        const addrParts = [c.address_line1,c.address_line2,c.city,c.pincode,c.state,c.country].filter(Boolean);
        return (
          <div key={c.id} className="card" style={{marginBottom:12,padding:0,overflow:'hidden'}}>
            {/* Header */}
            <div style={{padding:'14px 16px',display:'flex',alignItems:'flex-start',gap:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,fontSize:15,color:'var(--text1)'}}>{c.name}</span>
                  {c.industry && <span className="pill pill-gray" style={{fontSize:10}}>{c.industry}</span>}
                  <span className={`pill ${c.type==='hierarchical'?'pill-purple':'pill-gray'}`} style={{fontSize:10}}>{c.type==='hierarchical'?'Hierarchical':'Standard'}</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:14,marginTop:6,flexWrap:'wrap'}}>
                  {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'var(--accent)',textDecoration:'none'}}>🌐 {c.website.replace(/^https?:\/\//,'')}</a>}
                  {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'var(--accent)',textDecoration:'none'}}>in LinkedIn</a>}
                  {c.gstin && <span style={{fontSize:11,color:'var(--text2)'}}>GST: {c.gstin}</span>}
                  {c.pan && <span style={{fontSize:11,color:'var(--text2)'}}>PAN: {c.pan}</span>}
                </div>
                {addrParts.length>0 && (
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>📍 {addrParts.join(', ')}</div>
                )}
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0,flexWrap:'wrap',justifyContent:'flex-end'}}>
                <button className="btn-sm" style={{fontSize:11}} onClick={()=>setRateModal(c)}>Rate card</button>
                <button className="btn-sm" style={{fontSize:11}} onClick={()=>{ setEditClient(c); setEditForm(clientToForm(c)); setEditTab('Company'); }}>✎ Edit</button>
                <button className="btn-danger btn-sm" style={{fontSize:11}} onClick={()=>setDeleteClient(c.id)}>✕</button>
                <button className="btn-sm" style={{fontSize:11}} onClick={()=>setExpandedCards(p=>({...p,[c.id]:!p[c.id]}))}>
                  {expanded ? '▲ Less' : '▼ More'}
                </button>
              </div>
            </div>

            {/* Contacts row — always visible if contacts exist */}
            {hasContacts && (
              <div style={{padding:'0 16px 12px',display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:8}}>
                <ContactBlock label="Primary" name={c.contact_person} phone={c.phone} email={c.email}/>
                <ContactBlock label="Secondary" name={c.secondary_contact_name} phone={c.secondary_contact_phone} email={c.secondary_contact_email}/>
                <ContactBlock label="Accounts" name={c.accounts_contact_name} phone={c.accounts_contact_phone} email={c.accounts_contact_email}/>
                <ContactBlock label="Marketing" name={c.marketing_contact_name} phone={c.marketing_contact_phone} email={c.marketing_contact_email}/>
                <ContactBlock label="Decision Maker" name={c.decision_maker_name} phone={c.decision_maker_phone} email={c.decision_maker_email} birthday={c.decision_maker_birthday}/>
              </div>
            )}

            {/* Expanded: notes + hierarchy */}
            {expanded && <>
              {c.notes && (
                <div style={{padding:'10px 16px',background:'var(--surface2)',borderTop:'1px solid var(--border)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Notes</div>
                  <div style={{fontSize:12,color:'var(--text2)',whiteSpace:'pre-wrap'}}>{c.notes}</div>
                </div>
              )}
              {c.type==='hierarchical'&&(
                <div style={{padding:'10px 16px',borderTop:'1px solid var(--border)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Persons & Stakeholders</div>
                  {renderNodes(c.allNodes||[], null, c.id, 0, nodeHandlers)}
                  <button className="btn-sm" style={{marginTop:8,fontSize:11}} onClick={()=>{ setNodeModal({type:'add',clientId:c.id,parentId:null}); setNodeForm({name:'',label:'Person'}); }}>+ Add person</button>
                </div>
              )}
            </>}
          </div>
        );
      })}
      {!filtered.length && <div className="empty">{search ? 'No clients match your search.' : 'No clients yet. Click "+ Add client" to get started.'}</div>}

      {/* Add client modal */}
      {addModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-title">Add new client</div>
            <ClientForm form={addForm} setForm={setAddForm} tab={addTab} setTab={setAddTab}/>
            <div className="modal-actions">
              <button onClick={()=>{ setAddModal(false); setAddForm(EMPTY_FORM); }}>Cancel</button>
              <button className="btn-primary" onClick={()=>{ if(!addForm.name){toast.error('Enter client name');return;} addClient.mutate(addForm); }} disabled={addClient.isPending}>
                {addClient.isPending?'Creating…':'Create client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit client modal */}
      {editClient&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-title">Edit — {editClient.name}</div>
            <ClientForm form={editForm} setForm={setEditForm} tab={editTab} setTab={setEditTab}/>
            <div className="modal-actions">
              <button onClick={()=>setEditClient(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>{ if(!editForm.name){toast.error('Enter client name');return;} updateClient.mutate({id:editClient.id,...editForm}); }} disabled={updateClient.isPending}>
                {updateClient.isPending?'Saving…':'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Node modal */}
      {nodeModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-title">{nodeModal.type==='add'?'Add person / stakeholder':'Rename node'}</div>
            <div className="field"><label>Name</label><input value={nodeForm.name} onChange={e=>setNodeForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Rahul Mehta" autoFocus/></div>
            <div className="field"><label>Label</label><input value={nodeForm.label} onChange={e=>setNodeForm(f=>({...f,label:e.target.value}))} placeholder="Person / Stakeholder / Team"/></div>
            <div className="modal-actions">
              <button onClick={()=>setNodeModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={submitNode} disabled={addNode.isPending||editNode.isPending}>
                {(addNode.isPending||editNode.isPending)?'Saving…':nodeModal.type==='add'?'Add':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate card modal */}
      {rateModal&&(()=>{
        const clientCats = rateCategories.filter(c=>c.client_id===rateModal.id);
        const globalCats = rateCategories.filter(c=>!c.client_id);
        return (
          <div className="modal-overlay">
            <div className="modal" style={{maxWidth:580}}>
              <div className="modal-title">Rate card — {rateModal.name}</div>
              <div style={{display:'flex',gap:8,marginBottom:16}}>
                {[['categories','Client categories'],['overrides','Price overrides']].map(([k,l])=>(
                  <button key={k} className={rateTab===k?'btn-primary btn-sm':'btn btn-sm'} onClick={()=>setRateTab(k)}>{l}</button>
                ))}
              </div>
              {rateTab==='categories'&&<>
                <p style={{fontSize:12,color:'var(--text2)',marginBottom:10}}>
                  These are separate categories that exist <strong>only for {rateModal.name}</strong> — not shared with any other client, and not the same as adjusting a shared category's price (that's what the "Price overrides" tab is for).
                </p>
                {clientCats.length>0&&(
                  <table style={{width:'100%',fontSize:12,marginBottom:14}}>
                    <thead><tr><th style={{textAlign:'left'}}>Name</th><th>Price</th><th>GST%</th><th style={{textAlign:'right'}}></th></tr></thead>
                    <tbody>{clientCats.map(c=>{
                      const isEditing = editingCatId===c.id;
                      return isEditing ? (
                        <tr key={c.id}>
                          <td style={{padding:'5px 4px 5px 0'}}><input value={editCatForm.name} onChange={e=>setEditCatForm(f=>({...f,name:e.target.value}))} style={{width:'100%',fontSize:12,padding:'3px 6px'}}/></td>
                          <td style={{padding:'5px 4px'}}><input type="number" value={editCatForm.default_price} onChange={e=>setEditCatForm(f=>({...f,default_price:e.target.value}))} style={{width:80,fontSize:12,padding:'3px 6px',textAlign:'center'}}/></td>
                          <td style={{padding:'5px 4px'}}><input type="number" value={editCatForm.gst_percent} onChange={e=>setEditCatForm(f=>({...f,gst_percent:e.target.value}))} style={{width:56,fontSize:12,padding:'3px 6px',textAlign:'center'}}/></td>
                          <td style={{textAlign:'right',whiteSpace:'nowrap'}}>
                            <button className="btn-primary btn-sm" style={{fontSize:10,marginRight:4}} disabled={editClientCat.isPending}
                              onClick={()=>{ if(!editCatForm.name.trim()||!editCatForm.default_price){toast.error('Name and price required');return;} editClientCat.mutate({id:c.id,...editCatForm}); }}>
                              ✓ Save
                            </button>
                            <button className="btn-sm" style={{fontSize:10}} onClick={()=>setEditingCatId(null)}>✕</button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={c.id}>
                          <td style={{padding:'5px 0'}}>{c.name}</td>
                          <td style={{textAlign:'center'}}>₹{c.default_price}</td>
                          <td style={{textAlign:'center'}}>{c.gst_percent}%</td>
                          <td style={{textAlign:'right',whiteSpace:'nowrap'}}>
                            <button className="btn-sm" style={{fontSize:10,marginRight:4}} onClick={()=>{ setEditingCatId(c.id); setEditCatForm({name:c.name,default_price:c.default_price,gst_percent:c.gst_percent}); }}>✎ Edit</button>
                            <button className="btn-danger btn-sm" style={{fontSize:10}} onClick={()=>removeClientCat.mutate(c.id)}>✕</button>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                )}
                <div style={{fontSize:11,color:'var(--text3)',fontWeight:700,marginBottom:8}}>ADD CATEGORIES</div>
                {bulkRows.map((row,i)=>(
                  <div key={i} className="form-grid" style={{alignItems:'center',marginBottom:6}}>
                    <div className="field" style={{marginBottom:0}}><input value={row.name} onChange={e=>setBulkRows(p=>p.map((r,j)=>j===i?{...r,name:e.target.value}:r))} placeholder="Category name"/></div>
                    <div className="field" style={{marginBottom:0}}><input type="number" value={row.default_price} onChange={e=>setBulkRows(p=>p.map((r,j)=>j===i?{...r,default_price:e.target.value}:r))} placeholder="Price (₹)"/></div>
                    <div className="field" style={{marginBottom:0,width:70}}><input type="number" value={row.gst_percent} onChange={e=>setBulkRows(p=>p.map((r,j)=>j===i?{...r,gst_percent:e.target.value}:r))} placeholder="GST%"/></div>
                    {bulkRows.length>1&&<button className="btn-danger btn-sm" style={{fontSize:11}} onClick={()=>setBulkRows(p=>p.filter((_,j)=>j!==i))}>✕</button>}
                  </div>
                ))}
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button className="btn-sm" onClick={()=>setBulkRows(p=>[...p,{name:'',default_price:'',gst_percent:'18'}])}>+ Add row</button>
                  <button className="btn-primary btn-sm" onClick={saveBulkRows} disabled={addClientCat.isPending}>Save categories</button>
                </div>
              </>}
              {rateTab==='overrides'&&<>
                <p style={{fontSize:12,color:'var(--text2)',marginBottom:14}}>
                  For categories you already use across <strong>all</strong> clients (e.g. "Logo", "Banner") — this lets you charge <strong>{rateModal.name}</strong> a different price than everyone else, without creating a duplicate category. The category itself stays shared; only the price changes for this client.
                </p>
                <div className="field"><label>Category</label>
                  <SearchableSelect value={rateForm.category_id} onChange={id=>setRateForm(f=>({...f,category_id:id}))}
                    items={globalCats} getLabel={c=>`${c.name} (default ₹${c.default_price})`} placeholder="Select category" />
                </div>
                <div className="field"><label>Custom price (₹)</label>
                  <input type="number" value={rateForm.unit_price} onChange={e=>setRateForm(f=>({...f,unit_price:e.target.value}))} placeholder="e.g. 700"/>
                </div>
                <button className="btn-primary btn-sm" onClick={()=>{ if(!rateForm.category_id||!rateForm.unit_price){toast.error('Select category and enter price');return;} upsertRate.mutate({client_id:rateModal.id,...rateForm}); }} disabled={upsertRate.isPending}>Save override</button>
              </>}
              <div className="modal-actions" style={{marginTop:16}}>
                <button onClick={()=>{ setRateModal(null); setBulkRows([{name:'',default_price:'',gst_percent:'18'}]); setEditingCatId(null); }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog open={!!deleteClient} title="Remove client?" message="This will hide the client. Existing entries and invoices are not affected." confirmLabel="Remove" onConfirm={()=>removeClient.mutate(deleteClient)} onCancel={()=>setDeleteClient(null)}/>
      <ConfirmDialog open={!!deleteNode} title={`Remove "${deleteNode?.name}"?`} message="Child nodes will also be removed." confirmLabel="Remove" onConfirm={()=>removeNode.mutate(deleteNode)} onCancel={()=>setDeleteNode(null)}/>
    </div>
  );
}
